<template>
  <div v-if="product">
    <div class="product-details-container">
      <div class="product-image-container">
        <img
          :src="product.image_url || imgDomain + product.img_url"
          :alt="product.name || product.title"
          class="product-image"
        />
      </div>
      <div class="product-info-container">
        <h1 class="product-title">{{ product.name || product.title }}</h1>
        <p v-if="product.type && type == 'item'" class="product-type">
          {{ $t(`Type:`) }} {{ product.type }}
        </p>
        <p v-if="product.type && type == 'pack'" class="product-type">
          {{ $t(`Type: Pack`) }}
        </p>
        <p v-if="product.quality" class="product-quality">
          Quality: {{ product.quality }}
        </p>
        <div
          class="price-container"
          :class="{
            'has-discount': hasDiscount(product),
          }"
        >
          <template v-if="hasDiscount(product)">
            <div class="old-price">
              {{ product.old_price }} {{ currencySymbol }}
            </div>
            <div class="price desc discounted">
              <span>
                <b>{{ product.price }} {{ currencySymbol }}</b>
              </span>
            </div>
          </template>
          <template v-else>
            <div class="price desc">
              <span>
                <b>{{ product.price }} {{ currencySymbol }}</b>
              </span>
            </div>
          </template>
        </div>

        <div
          v-if="type == 'item' && chartData.length > 0"
          class="chart-section"
        >
          <h3 class="chart-title">{{ $t(`Scale`) }}</h3>
          <div class="chart-container">
            <canvas ref="chartCanvas"></canvas>
          </div>
        </div>

        <div
          class="content_desc"
          v-if="product.type && type == 'pack'"
          v-html="product.content_desc"
        ></div>

        <button class="add-to-cart-button button" @click="addToCart(product)">
          {{ $t(`Add to cart`) }}
        </button>
        <transition>
          <div v-if="chosenItem" class="desc green">
            {{ $t("Added") }}
          </div>
        </transition>

        <div class="disclaimer" v-if="type == 'pack'">
          <img src="@/assets/img/tooltip.svg" alt="" />
          <p>
            {{
              $t(`product_disclaimer`)
            }}
          </p>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="loadError" class="product-load-error">
    <p>{{ $t("Could not load the product. Please try again.") }}</p>
    <button class="button primary" @click="fetchProductData">
      {{ $t("Try again") }}
    </button>
  </div>
  <div v-else>{{ $t(`Loading...`) }}</div>
</template>

<script>
import { mapGetters } from "vuex";
import { Chart, registerables } from "chart.js";
import { hasDiscount } from "../utils/price";

Chart.register(...registerables);
Chart.register({
  id: "customOffset",
  beforeUpdate: function (chart) {
    const offset = chart.options.customOffset || 0;
    const yAxis = chart.scales.y;
    const datasets = chart.data.datasets;

    let max = Math.max(...datasets[0].data);
    let min = Math.min(...datasets[0].data);

    yAxis.options.max = max + offset;
    yAxis.options.min = min - offset;
  },
});
export default {
  name: "ProductPage",
  props: {
    type: {
      type: String,
    },
    id: {
      type: String,
    },
    chosenItem: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      product: null,
      loadError: false,
      chart: null,
      chartData: [],
      imgDomain: "",
    };
  },
  computed: {
    ...mapGetters("app", ["currencyCode", "currencySymbol"]),
  },
  created() {
    this.fetchProductData();
  },
  mounted() {
    this.imgDomain = process.env.VUE_APP_STATIC_DOMAIN;
  },
  watch: {
    currencyCode() {
      this.fetchProductData();
    },
    chartData(newData) {
      if (newData.length > 0) {
        this.createChart();
      }
    },
  },
  methods: {
    hasDiscount,
    addToCart(item) {
      this.$emit("addToCart", item.id, item.item_type);
    },
    fetchProductData() {
      this.loadError = false;
      const apiEndpoint =
        this.type === "pack" ? `packs/${this.id}` : `items/${this.id}`;
      this.$http
        .get(`${apiEndpoint}`, {
          params: { currency: this.currencyCode, include_price_history: true },
        })
        .then((res) => {
          this.product = res.data.payload;
          // this.chartData = this.product?.price_history || [];
          // this.chartData = [
          //     { date: 'Dec 10 2024', price: 250, count: 1 },
          //     { date: 'Dec 21 2024', price: 280, count: 1 },
          //     { date: 'Jan 1 2024', price: 300, count: 1 },
          //     { date: 'Jan 12 2024', price: 320, count: 2 },
          //     { date: 'Jan 24 2024', price: 310, count: 1 },
          //     { date: 'Feb 4 2024', price: 330, count: 1 },
          //     { date: 'Feb 16 2024', price: 350, count: 1 },
          //     { date: 'Feb 27 2024', price: 340, count: 3 },
          // ]
        })
        .catch((error) => {
          console.error("Failed to load product data:", error);
          this.loadError = true;
        });
    },
    createChart() {
      this.$nextTick(() => {
        const canvas = this.$refs.chartCanvas;
        if (canvas && this.chartData && this.chartData.length > 0) {
          this.destroyChart();
          const ctx = canvas.getContext("2d");

          this.chart = new Chart(ctx, {
            type: "line",
            data: {
              labels: this.chartData.map((data) => data.date),
              datasets: [
                {
                  label: "Price",
                  data: this.chartData.map((data) => data.price),
                  borderColor: "#19D6CC",
                  borderWidth: 2,
                  pointBackgroundColor: "#19D6CC",
                  pointBorderColor: "#19D6CC",
                  pointRadius: 0,
                  pointHoverRadius: 5,
                  tension: 0,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              layout: {
                padding: {
                  right: 14,
                },
              },
              scales: {
                y: {
                  ticks: {
                    padding: 0,
                    font: {
                      family: "Manrope",
                      size: 14,
                      weight: 500,
                    },
                    count: 5,
                    color: "white",
                    callback: (value) => `${value.toFixed(2)} €`,
                  },
                  grid: {
                    color: "#46566A",
                    lineWidth: 1,
                    drawBorder: true,
                    drawOnChartArea: true,
                    drawTicks: true,
                    borderRadius: 4,
                  },
                  afterDataLimits: (axis) => {
                    const paddingPixels = 30;
                    const chartHeight = axis.chart.height;
                    const valueRange = axis.max - axis.min;
                    const pixelsPerValue = chartHeight / valueRange;
                    const paddingValue = paddingPixels / pixelsPerValue;

                    axis.max += paddingValue;
                    axis.min -= paddingValue;
                  },
                },
                x: {
                  grid: {
                    color: "#46566A",
                    lineWidth: 1,
                    drawBorder: true,
                    drawOnChartArea: true,
                    drawTicks: true,
                  },
                  border: {
                    display: false,
                    color: "#46566A",
                    width: 1,
                  },
                  ticks: {
                    autoSkip: true,
                    maxTicksLimit: 8,
                    font: {
                      family: "Manrope",
                      size: 14,
                      weight: 500,
                    },
                    color: "white",
                    callback: (index) => {
                      return this.chartData[index].date.slice(0, -4);
                    },
                  },
                },
              },
              plugins: {
                legend: {
                  display: false,
                },
                tooltip: {
                  backgroundColor: "#16171C",
                  titleFont: {
                    family: "Manrope",
                    size: 14,
                    weight: 500,
                  },
                  bodyFont: {
                    family: "Manrope",
                    size: 14,
                    weight: 500,
                  },
                  padding: 10,
                  cornerRadius: 8,
                  displayColors: false,
                  callbacks: {
                    title: (tooltipItems) => {
                      const item = tooltipItems[0];
                      return `${item.label}`;
                    },
                    label: (context) => {
                      return `CDN ${context.parsed.y.toFixed(2)} €`;
                    },
                    afterLabel: (context) => {
                      const dataPoint = this.chartData[context.dataIndex];
                      return `${dataPoint.count} sold`;
                    },
                  },
                },
              },
              elements: {
                point: {
                  radius: 1,
                  hoverRadius: 8,
                  hitRadius: 20,
                },
              },
            },
          });
        }
      });
    },

    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.product-details-container {
  display: flex;
  max-width: 1240px;
  min-height: 618px;
  margin: 0 auto;
  padding: 24px 10px 95px 10px;
}

.product-image-container {
  padding-top: 16px;
  width: 572px;
  height: 426px;

  border-radius: 16px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.product-image {
  display: inline-block;
  max-width: 442px;
  max-height: 100%;
  -o-object-fit: contain;
  object-fit: contain;
  width: 100%;
  max-height: 332px;
}

.product-info-container {
  flex: 1;
  padding-left: 47px;
}

.product-title {
  font-size: 48px;
  font-weight: 700;
  line-height: 68.64px;
  text-align: left;
  color: white;
  margin-bottom: 12px;
}

.product-type,
.product-quality {
  font-size: 16px;
  font-weight: 400;
  line-height: 22.88px;
  text-align: left;
  color: white;
  margin-bottom: 8px;
}

.price-container {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: 4px;
  height: 26px;
}

.price-container.has-discount {
  gap: 8px;
  align-items: center;
}

.old-price {
  font-family: "Quantico", sans-serif;
  font-size: 16px;
  font-weight: 700;
  text-align: left;
  color: white;
  text-decoration: line-through;
}

.price {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  text-align: left;
}

.price.discounted {
  text-align: right;
  color: #e63333;
}

.chart-section {
  margin-bottom: 26px;
  border: 1px solid #46566a;
  background-color: #1e2949;
  padding: 16px;
  border-radius: 8px;
}

.chart-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 22.88px;
  text-align: left;
  color: white;
  margin-bottom: 8px;
}

.chart-container {
  background: #1e2949;
  height: 300px;
  width: 100%;
  position: relative;
}

.content_desc {
  margin-top: 20px;
  color: white;
}

.add-to-cart-button {
  margin-top: 24px;
  max-width: 273px;
  border-radius: 40px;
}

.desc.green {
  text-align: center;
  max-width: 273px;
  width: 100%;
}

.disclaimer {
  color: white;
  margin-top: 20px;
  font-size: 12px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  text-align: left;

  h2 {
    font-size: 14px;
  }
}

@media (max-width: 1220px) {
  .product-details-container {
    flex-direction: column;
    align-items: center;
  }

  .product-image-container {
    padding-top: 0;
    width: 100%;
    max-width: 572px;
    height: auto;
    aspect-ratio: 572 / 426;
  }

  .product-info-container {
    padding-left: 0;
    width: 100%;
    max-width: 572px;
  }
}
</style>
